import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const SPEED_IN_PIXELS_PER_SECOND = 40;

/**
 * A continuously looping row of customer stories. The story under the pointer,
 * in focus, or tapped grows and pauses the row. Videos play with it by default;
 * with data-playback="button" they stay paused until the viewer presses play.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The moving row of stories.
 *
 * @extends Component<Refs>
 */
class CustomerStoriesComponent extends Component {
  requiredRefs = ['track'];

  /** @type {HTMLElement | null} */
  #active = null;
  /** @type {AbortController | null} */
  #listeners = null;
  /** @type {HTMLButtonElement | null} */
  #playingButton = null;
  /** @type {number} */
  #builtWidth = 0;

  get #usesButtons() {
    return this.dataset.playback === 'button';
  }

  connectedCallback() {
    super.connectedCallback();
    this.#listen();
    this.#build();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listen();
    this.#build();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#handleResize.cancel?.();
  }

  #listen() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();

    const { signal } = this.#listeners;
    const { track } = this.refs;

    track.addEventListener('pointerover', this.#handlePointerOver, { signal });
    track.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    track.addEventListener('focusin', this.#handleFocusIn, { signal });
    track.addEventListener('focusout', this.#handleFocusOut, { signal });
    track.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener('pointerdown', this.#handleOutsideTap, { signal });
    window.addEventListener('resize', this.#handleResize, { signal });
  }

  /** Repeats the stories until the loop fills the screen, then measures one full set. */
  #build() {
    const { track } = this.refs;

    this.#stopPlayback();
    this.#setActive(null);
    this.#builtWidth = this.offsetWidth;
    track.removeAttribute('data-running');
    for (const clone of track.querySelectorAll('[data-story-clone]')) clone.remove();

    if (document.documentElement.classList.contains('shopify-design-mode')) return;

    const stories = /** @type {HTMLElement[]} */ ([...track.querySelectorAll('.story-card')]);
    const [first] = stories;
    if (!first) return;

    const appendCopy = () => {
      /** @type {HTMLElement | undefined} */
      let firstCopy;

      for (const story of stories) {
        const copy = /** @type {HTMLElement} */ (story.cloneNode(true));

        copy.dataset.storyClone = '';
        copy.setAttribute('aria-hidden', 'true');
        for (const control of copy.querySelectorAll('a, button')) control.setAttribute('tabindex', '-1');
        track.appendChild(copy);
        firstCopy ??= copy;
      }

      return firstCopy;
    };

    const distance = (appendCopy()?.offsetLeft ?? first.offsetLeft) - first.offsetLeft;
    if (distance <= 0) return;

    const copiesNeeded = Math.ceil(this.offsetWidth / distance);
    for (let copy = 0; copy < copiesNeeded; copy++) appendCopy();

    track.style.setProperty('--story-distance', `${distance}px`);
    track.style.setProperty('--story-duration', `${Math.round(distance / SPEED_IN_PIXELS_PER_SECOND)}s`);
    track.setAttribute('data-running', '');
  }

  /** @param {HTMLElement | null} story - The story to activate. */
  #setActive(story) {
    if (story === this.#active) return;

    this.#active?.classList.remove('story-card--active');
    if (!this.#usesButtons) this.#active?.querySelector('video')?.pause();
    this.#active = story;
    this.#syncPaused();

    if (!story) return;

    story.classList.add('story-card--active');
    if (this.#usesButtons) return;

    // Browsers may refuse playback; the poster image stays visible
    story.querySelector('video')?.play()?.catch(() => {});
  }

  #syncPaused() {
    this.refs.track.toggleAttribute('data-paused', Boolean(this.#active || this.#playingButton));
  }

  /** @param {HTMLButtonElement} button - The playback button to show as playing or paused. */
  #setButtonState(button, playing) {
    button.toggleAttribute('data-playing', playing);
    button.setAttribute('aria-label', button.dataset[playing ? 'labelPause' : 'labelPlay'] ?? '');
  }

  #stopPlayback() {
    if (!this.#playingButton) return;

    this.#playingButton.closest('.story-card')?.querySelector('video')?.pause();
    this.#setButtonState(this.#playingButton, false);
    this.#playingButton = null;
    this.#syncPaused();
  }

  /**
   * @param {EventTarget | null} target - The event target.
   * @returns {HTMLElement | null} The story holding the target.
   */
  #storyFrom(target) {
    return target instanceof Element ? target.closest('.story-card') : null;
  }

  /** @param {MouseEvent} event - The click event. */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('.story-card__playback');
    if (!(button instanceof HTMLButtonElement)) return;

    const video = button.closest('.story-card')?.querySelector('video');
    if (!video) return;

    const wasPlaying = button === this.#playingButton;
    this.#stopPlayback();
    if (wasPlaying) return;

    this.#playingButton = button;
    this.#setButtonState(button, true);
    this.#syncPaused();
    video.play().catch(() => this.#stopPlayback());
  };

  /** @param {PointerEvent} event - The pointer event. */
  #handlePointerOver = (event) => {
    const story = this.#storyFrom(event.target);
    if (story) this.#setActive(story);
  };

  /** @param {PointerEvent} event - The pointer event. */
  #handlePointerLeave = (event) => {
    if (event.pointerType === 'mouse') this.#setActive(null);
  };

  /** @param {FocusEvent} event - The focus event. */
  #handleFocusIn = (event) => {
    this.#setActive(this.#storyFrom(event.target));
  };

  /** @param {FocusEvent} event - The focus event. */
  #handleFocusOut = (event) => {
    if (!this.#storyFrom(event.relatedTarget)) this.#setActive(null);
  };

  /** @param {PointerEvent} event - The pointer event. */
  #handleOutsideTap = (event) => {
    if (event.pointerType !== 'mouse' && !this.#storyFrom(event.target)) this.#setActive(null);
  };

  #handleResize = debounce(() => {
    if (this.offsetWidth !== this.#builtWidth) this.#build();
  }, 250);
}

if (!customElements.get('customer-stories-component')) {
  customElements.define('customer-stories-component', CustomerStoriesComponent);
}
